import { AlertCircle, CheckCircle2 } from 'lucide-react';
import type { FieldErrors } from 'react-hook-form';
import { cn } from '../../lib/utils';
import type { FormFieldSchema } from '../types';

interface StatusTabProps {
  isValid: boolean;
  isDirty: boolean;
  errors: FieldErrors<Record<string, unknown>>;
  touchedFields: Record<string, unknown>;
  fields: FormFieldSchema[];
}

export function StatusTab({ isValid, isDirty, errors, touchedFields, fields }: StatusTabProps) {
  return (
    <div className="h-full m-0 p-6 overflow-y-auto custom-scrollbar space-y-6">
      {/* Validity Summary */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Form Health
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg border bg-card shadow-sm">
            <div className="text-[10px] text-muted-foreground uppercase font-bold">Valid</div>
            <div
              className={cn(
                'text-lg font-bold mt-1',
                isValid ? 'text-green-500' : 'text-amber-500'
              )}
            >
              {isValid ? 'YES' : 'NO'}
            </div>
          </div>
          <div className="p-3 rounded-lg border bg-card shadow-sm">
            <div className="text-[10px] text-muted-foreground uppercase font-bold">Dirty</div>
            <div
              className={cn(
                'text-lg font-bold mt-1',
                isDirty ? 'text-blue-500' : 'text-muted-foreground/30'
              )}
            >
              {isDirty ? 'YES' : 'NO'}
            </div>
          </div>
        </div>
      </div>

      {/* Errors List */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Validation Errors
        </h3>
        {Object.keys(errors).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(errors).map(([key, error]) => {
              const field = fields.find((f) => f.id === key);
              return (
                <div
                  key={key}
                  className="p-3 rounded-lg bg-destructive/5 border border-destructive/20 flex items-start gap-3"
                >
                  <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-destructive">{field?.label || key}</p>
                    <p className="text-[10px] text-destructive/80 mt-0.5">
                      {error?.message || 'Validation failed'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 rounded-lg border border-dashed text-center flex flex-col items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-green-500/30" />
            <p className="text-[10px] text-muted-foreground">No active errors</p>
          </div>
        )}
      </div>

      {/* Interaction Log */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Interaction Log
        </h3>
        <div className="p-3 rounded-lg border bg-card text-[10px] space-y-2">
          <div className="flex justify-between border-b pb-1 border-border/50">
            <span>Touched Fields:</span>
            <span className="font-mono text-blue-500">{Object.keys(touchedFields).length}</span>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {Object.keys(touchedFields).map((key) => (
              <span key={key} className="px-1.5 py-0.5 bg-blue-500/10 text-blue-600 rounded">
                {fields.find((f) => f.id === key)?.label || key}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
